import { ProfileData } from "./types";
import CustomSelect from "../../components/Layout/CustomSelect";
import { UsageStats } from "./UsageStats";
import { MemorySection } from "./MemorySection";
import { useMemoryHandler } from "../../hooks/useMemoryHandler";
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";

export const ProfileSection = ({
  setIsLoading
}: {
  setIsLoading: (isLoading: boolean) => void;
}) => {
  const { user, updateUser } = useAuth();

  const [profileData, setProfileData] = useState<ProfileData>({
    name: user?.name || '',
    s_name: user?.s_name || '',
    s_level: user?.s_level || '',
  });
  const [originalProfileData, setOriginalProfileData] = useState<ProfileData>({
    name: '',
    s_name: '',
    s_level: '',
  });


  const { 
    localMemoryArray, 
    expandedMemoryIndex, 
    setExpandedMemoryIndex,
    hasChanges,
    handleMemoryChange,
    handleMemorySubmit,
    handleAddMemory,
    handleRemoveMemory
  } = useMemoryHandler();

  const toast = useToast();

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const initialData: ProfileData = {
      name: user?.name || '',
      s_name: user?.s_name || '',
      s_level: user?.s_level || '',
    };
    setProfileData(initialData);
    setOriginalProfileData(initialData);
    setIsLoading(false);
  }, [user, setIsLoading]);

  const hasProfileChanges = (): boolean => {
    if (!profileData.name.trim() || !profileData.s_name.trim()) {
      return false;
    }
    
    return originalProfileData.name !== profileData.name || 
           originalProfileData.s_name !== profileData.s_name ||
           originalProfileData.s_level !== profileData.s_level;
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profileData.name.trim() || !profileData.s_name.trim()) {
      toast('Name fields cannot be empty', 'error');
      return;
    }

    setIsLoading(true);
    try {
      updateUser({
        name: profileData.name,
        s_name: profileData.s_name,
        s_level: profileData.s_level,
      });
      toast('Profile updated successfully', 'success');
      setOriginalProfileData(profileData);
    } catch (error) {
      toast('Failed to update profile', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  
  if (!user) return (
    <div>
      <p>Loading...</p>
    </div>
  );

  const studyLevelOptions = [
    { value: 'high_school', label: 'High School' },
    { value: 'undergraduate', label: 'Undergraduate' },
    { value: 'graduate', label: 'Graduate' },
    { value: 'phd', label: 'PhD' },
  ];

  return (
    <>
      <UsageStats />
      
      <div className="settings-item">
        <h3>Personal Information</h3>
        <form onSubmit={handleProfileSubmit} className="profile-form">
          <div className='profile-group-wrap'>
            <div className="profile-form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={profileData.name}
                onChange={handleProfileChange}
              />
            </div>
            <div className="profile-form-group">
              <label htmlFor="s_name">What StudyBuddy calls you?</label>
              <input
                type="text"
                id="s_name"
                name="s_name"
                value={profileData.s_name}
                onChange={handleProfileChange}
              />
            </div>
          </div>
          <div className="profile-form-group">
            <label htmlFor="email">Email (cannot be changed)</label>
            <input
              type="email"
              id="email"
              name="email"
              value={user?.email || ''}
              disabled
            />
          </div>
          <div className="profile-form-group">
            <label htmlFor="s_level">Study Level</label>
            <CustomSelect
              options={studyLevelOptions}
              value={profileData.s_level}
              onChange={(value) => handleSelectChange('s_level', value)}
              placeholder="Select Study Level"
            />
          </div>
          {hasProfileChanges() && (
            <button type="submit" className="save-button">
              Save
            </button>
          )}
        </form>
      </div>
      
      <MemorySection
        localMemoryArray={localMemoryArray}
        expandedMemoryIndex={expandedMemoryIndex}
        setExpandedMemoryIndex={setExpandedMemoryIndex}
        handleMemoryChange={handleMemoryChange}
        handleMemorySubmit={handleMemorySubmit}
        handleAddMemory={handleAddMemory}
        handleRemoveMemory={handleRemoveMemory}
        hasChanges={hasChanges}
      />
    </>
  );
}; 